import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Eye, EyeOff, Pencil, Calendar } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const emptyForm = {
  title_fr: "", title_en: "",
  excerpt_fr: "", excerpt_en: "",
  content_fr: "", content_en: "",
  image_url: "", category: "",
  published: true,
};

const BlogManager = ({ tAdmin }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blog");
      setPosts(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.put(`/admin/blog/${editId}`, form);
        toast.success(tAdmin.blog.updated);
      } else {
        await api.post("/admin/blog", form);
        toast.success(tAdmin.blog.created);
      }
      setForm(emptyForm);
      setShowForm(false);
      setEditId(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setForm({
      title_fr: p.title_fr || "", title_en: p.title_en || "",
      excerpt_fr: p.excerpt_fr || "", excerpt_en: p.excerpt_en || "",
      content_fr: p.content_fr || "", content_en: p.content_en || "",
      image_url: p.image_url || "", category: p.category || "",
      published: !!p.published,
    });
    setShowForm(true);
  };

  const togglePublish = async (p) => {
    try {
      await api.put(`/admin/blog/${p.id}`, { published: !p.published });
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(tAdmin.blog.confirmDelete.replace("{title}", p.title_fr))) return;
    try {
      await api.delete(`/admin/blog/${p.id}`);
      toast.success(tAdmin.blog.deleted);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="blog-manager">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-[#002B49] font-semibold">{tAdmin.blog.title}</h2>
          <p className="text-sm text-[#64748B]">{tAdmin.blog.subtitle}</p>
        </div>
        {!showForm && (
          <button
            onClick={() => { setEditId(null); setForm(emptyForm); setShowForm(true); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#002B49] text-white hover:bg-[#001F35] text-sm font-medium"
            data-testid="blog-add-btn"
          >
            <Plus size={14} /> {tAdmin.blog.addPost}
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-2xl border border-black/5 p-6 mb-6" data-testid="blog-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={tAdmin.blog.fTitleFr}><input required value={form.title_fr} onChange={e => setForm({...form, title_fr: e.target.value})} className="form-input" data-testid="blog-title-fr" /></Field>
            <Field label={tAdmin.blog.fTitleEn}><input required value={form.title_en} onChange={e => setForm({...form, title_en: e.target.value})} className="form-input" data-testid="blog-title-en" /></Field>
            <Field label={tAdmin.blog.fImage} className="sm:col-span-2"><input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} className="form-input" data-testid="blog-image" /></Field>
            <Field label={tAdmin.blog.fCategory}><input value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="form-input" data-testid="blog-category" /></Field>
            <Field label={tAdmin.blog.fPublished}>
              <select value={form.published ? "1" : "0"} onChange={e => setForm({...form, published: e.target.value === "1"})} className="form-input">
                <option value="1">{tAdmin.blog.yes}</option>
                <option value="0">{tAdmin.blog.no}</option>
              </select>
            </Field>
            <Field label={tAdmin.blog.fExcerptFr} className="sm:col-span-2"><textarea required rows="2" value={form.excerpt_fr} onChange={e => setForm({...form, excerpt_fr: e.target.value})} className="form-input" data-testid="blog-excerpt-fr" /></Field>
            <Field label={tAdmin.blog.fExcerptEn} className="sm:col-span-2"><textarea required rows="2" value={form.excerpt_en} onChange={e => setForm({...form, excerpt_en: e.target.value})} className="form-input" data-testid="blog-excerpt-en" /></Field>
            <Field label={tAdmin.blog.fContentFr} className="sm:col-span-2"><textarea required rows="6" value={form.content_fr} onChange={e => setForm({...form, content_fr: e.target.value})} className="form-input font-mono text-sm" data-testid="blog-content-fr" /></Field>
            <Field label={tAdmin.blog.fContentEn} className="sm:col-span-2"><textarea required rows="6" value={form.content_en} onChange={e => setForm({...form, content_en: e.target.value})} className="form-input font-mono text-sm" data-testid="blog-content-en" /></Field>
          </div>
          <div className="flex gap-2 mt-5">
            <button type="submit" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#002B49] text-white hover:bg-[#001F35] text-sm font-medium" data-testid="blog-save"><Save size={14} /> {editId ? tAdmin.blog.save : tAdmin.blog.create}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditId(null); setForm(emptyForm); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-black/10 text-[#64748B] text-sm"><X size={14} /> {tAdmin.blog.cancel}</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#64748B]">…</div>
        ) : posts.length === 0 ? (
          <div className="p-12 text-center text-[#64748B]" data-testid="blog-empty">{tAdmin.blog.empty}</div>
        ) : (
          <ul className="divide-y divide-black/5">
            {posts.map((p, i) => (
              <li key={p.id} className="p-5 flex items-center gap-4 flex-wrap" data-testid={`blog-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#002B49]">{p.title_fr}</span>
                    {p.category && <span className="text-[10px] uppercase tracking-wider text-[#C5A059] bg-[#FDF8EE] px-2 py-0.5 rounded-full">{p.category}</span>}
                    {!p.published && <span className="text-[10px] uppercase tracking-wider text-[#64748B] bg-black/5 px-2 py-0.5 rounded-full">{tAdmin.blog.draft}</span>}
                  </div>
                  <div className="text-xs text-[#64748B] mt-0.5 inline-flex items-center gap-1.5"><Calendar size={11} /> {new Date(p.created_at).toLocaleDateString()} · /{p.slug}</div>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => togglePublish(p)} className="p-2 rounded-md hover:bg-black/5 text-[#64748B]" data-testid={`blog-toggle-${i}`}>{p.published ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                  <button onClick={() => startEdit(p)} className="p-2 rounded-md hover:bg-black/5 text-[#64748B]" data-testid={`blog-edit-${i}`}><Pencil size={14} /></button>
                  <button onClick={() => remove(p)} className="p-2 rounded-md hover:bg-red-50 text-red-600" data-testid={`blog-delete-${i}`}><Trash2 size={14} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <style>{`
        .form-input { width:100%; margin-top:.4rem; padding:.6rem .85rem; border-radius:.45rem; border:1px solid #E5E7EB; outline:none; transition:border-color .2s, box-shadow .2s; font-size:.9rem; color:#0A192F; background:#fff; }
        .form-input:focus { border-color:#002B49; box-shadow:0 0 0 3px rgba(0,43,73,.1); }
      `}</style>
    </div>
  );
};

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-[11px] font-medium text-[#64748B] uppercase tracking-wider">{label}</span>
    {children}
  </label>
);

export default BlogManager;
